import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CreditCard, User, Loader2, Smartphone, Eye, EyeOff } from "lucide-react";

interface SimCard {
  id: string;
  sim_number: string;
  phone_number: string | null;
  provider: string;
  tariff: string | null;
  pin: string | null;
  puk: string | null;
  monthly_cost: number;
  contract_start_date: string | null;
  contract_end_date: string | null;
  data_volume: string | null;
  assigned_employee_id: string | null;
  assigned_device_id: string | null;
  assigned_at: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
}

interface Employee {
  id: string;
  name: string;
  position: string;
}

interface MobileDevice {
  id: string;
  phone_type: string;
  model: string | null;
  manufacturer: string | null;
}

const SIM_PROVIDERS = [
  "Telekom",
  "Vodafone",
  "O2",
  "1&1",
  "Congstar",
  "Aldi Talk",
  "Lidl Connect",
  "Sonstiger",
];

export default function SimCardSettings() {
  const { user } = useAuth();
  const [simCards, setSimCards] = useState<SimCard[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [devices, setDevices] = useState<MobileDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingSim, setEditingSim] = useState<SimCard | null>(null);
  const [simToDelete, setSimToDelete] = useState<SimCard | null>(null);
  const [simToAssign, setSimToAssign] = useState<SimCard | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [showPin, setShowPin] = useState(false);
  const [showPuk, setShowPuk] = useState(false);

  const [formData, setFormData] = useState({
    sim_number: "",
    phone_number: "",
    provider: "",
    tariff: "",
    pin: "",
    puk: "",
    monthly_cost: "",
    contract_start_date: "",
    contract_end_date: "",
    data_volume: "",
    assigned_employee_id: "",
    assigned_device_id: "",
    notes: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [simCardsRes, employeesRes, devicesRes] = await Promise.all([
        supabase
          .from("sim_cards")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("employees")
          .select("id, name, position")
          .is("archived_at", null)
          .order("name"),
        supabase
          .from("mobile_phones")
          .select("id, phone_type, model, manufacturer")
          .eq("is_active", true)
          .order("phone_type"),
      ]);

      if (simCardsRes.error) throw simCardsRes.error;
      if (employeesRes.error) throw employeesRes.error;
      if (devicesRes.error) throw devicesRes.error;

      setSimCards(simCardsRes.data || []);
      setEmployees(employeesRes.data || []);
      setDevices(devicesRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sim_number: "",
      phone_number: "",
      provider: "",
      tariff: "",
      pin: "",
      puk: "",
      monthly_cost: "",
      contract_start_date: "",
      contract_end_date: "",
      data_volume: "",
      assigned_employee_id: "",
      assigned_device_id: "",
      notes: "",
    });
    setEditingSim(null);
    setShowPin(false);
    setShowPuk(false);
  };

  const handleOpenDialog = (sim?: SimCard) => {
    if (sim) {
      setEditingSim(sim);
      setFormData({
        sim_number: sim.sim_number,
        phone_number: sim.phone_number || "",
        provider: sim.provider,
        tariff: sim.tariff || "",
        pin: sim.pin || "",
        puk: sim.puk || "",
        monthly_cost: sim.monthly_cost ? String(sim.monthly_cost) : "",
        contract_start_date: sim.contract_start_date || "",
        contract_end_date: sim.contract_end_date || "",
        data_volume: sim.data_volume || "",
        assigned_employee_id: sim.assigned_employee_id || "",
        assigned_device_id: sim.assigned_device_id || "",
        notes: sim.notes || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.sim_number.trim()) {
      toast.error("Bitte geben Sie die SIM-Nummer ein");
      return;
    }
    if (!formData.provider.trim()) {
      toast.error("Bitte wählen Sie einen Provider");
      return;
    }

    setSaving(true);
    try {
      const monthlyCost = formData.monthly_cost ? parseFloat(formData.monthly_cost) : 0;
      const assignedEmployeeId = formData.assigned_employee_id || null;
      const assignedDeviceId = formData.assigned_device_id || null;

      if (editingSim) {
        const { error } = await supabase
          .from("sim_cards")
          .update({
            sim_number: formData.sim_number,
            phone_number: formData.phone_number || null,
            provider: formData.provider,
            tariff: formData.tariff || null,
            pin: formData.pin || null,
            puk: formData.puk || null,
            monthly_cost: monthlyCost,
            contract_start_date: formData.contract_start_date || null,
            contract_end_date: formData.contract_end_date || null,
            data_volume: formData.data_volume || null,
            assigned_employee_id: assignedEmployeeId,
            assigned_device_id: assignedDeviceId,
            assigned_at: assignedEmployeeId !== editingSim.assigned_employee_id 
              ? (assignedEmployeeId ? new Date().toISOString() : null) 
              : editingSim.assigned_at,
            notes: formData.notes || null,
          })
          .eq("id", editingSim.id);

        if (error) throw error;
        toast.success("SIM-Karte wurde aktualisiert");
      } else {
        const { error } = await supabase.from("sim_cards").insert({
          sim_number: formData.sim_number,
          phone_number: formData.phone_number || null,
          provider: formData.provider,
          tariff: formData.tariff || null,
          pin: formData.pin || null,
          puk: formData.puk || null,
          monthly_cost: monthlyCost,
          contract_start_date: formData.contract_start_date || null,
          contract_end_date: formData.contract_end_date || null,
          data_volume: formData.data_volume || null,
          assigned_employee_id: assignedEmployeeId,
          assigned_device_id: assignedDeviceId,
          assigned_at: assignedEmployeeId ? new Date().toISOString() : null,
          notes: formData.notes || null,
          created_by: user?.id,
        });

        if (error) throw error;
        toast.success("SIM-Karte wurde hinzugefügt");
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error saving SIM card:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!simToDelete) return;

    try {
      const { error } = await supabase
        .from("sim_cards")
        .delete()
        .eq("id", simToDelete.id);

      if (error) throw error;
      toast.success("SIM-Karte wurde gelöscht");
      setDeleteDialogOpen(false);
      setSimToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting SIM card:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleAssign = async () => {
    if (!simToAssign) return;

    try {
      const { error } = await supabase
        .from("sim_cards")
        .update({
          assigned_employee_id: selectedEmployeeId || null,
          assigned_device_id: selectedDeviceId || null,
          assigned_at: selectedEmployeeId ? new Date().toISOString() : null,
        })
        .eq("id", simToAssign.id);

      if (error) throw error;
      toast.success("SIM-Karte wurde zugewiesen");
      setAssignDialogOpen(false);
      setSimToAssign(null);
      setSelectedEmployeeId("");
      setSelectedDeviceId("");
      loadData();
    } catch (error) {
      console.error("Error assigning SIM card:", error);
      toast.error("Fehler bei der Zuweisung");
    }
  };

  const openAssignDialog = (sim: SimCard) => {
    setSimToAssign(sim);
    setSelectedEmployeeId(sim.assigned_employee_id || "");
    setSelectedDeviceId(sim.assigned_device_id || "");
    setAssignDialogOpen(true);
  };

  const getEmployeeName = (employeeId: string | null) => {
    if (!employeeId) return null;
    const employee = employees.find((e) => e.id === employeeId);
    return employee?.name || "Unbekannt";
  };

  const getDeviceName = (deviceId: string | null) => {
    if (!deviceId) return null;
    const device = devices.find((d) => d.id === deviceId);
    return device ? `${device.phone_type}${device.model ? ` (${device.model})` : ''}` : "Unbekannt";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate totals
  const totalMonthly = simCards.reduce((sum, s) => sum + (s.monthly_cost || 0), 0);
  const assignedCount = simCards.filter((s) => s.assigned_employee_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Gesamt SIM-Karten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{simCards.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Zugewiesen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Monatliche Kosten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalMonthly)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          SIM-Karte hinzufügen
        </Button>
      </div>

      {/* SIM Card List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SIM-Nummer</TableHead>
                <TableHead>Telefonnummer</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Tarif</TableHead>
                <TableHead className="text-right">Monatl. Kosten</TableHead>
                <TableHead>Gerät</TableHead>
                <TableHead>Mitarbeiter</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {simCards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    Keine SIM-Karten vorhanden
                  </TableCell>
                </TableRow>
              ) : (
                simCards.map((sim) => (
                  <TableRow key={sim.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{sim.sim_number}</span>
                      </div>
                    </TableCell>
                    <TableCell>{sim.phone_number || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{sim.provider}</Badge>
                    </TableCell>
                    <TableCell>{sim.tariff || "-"}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(sim.monthly_cost || 0)}
                    </TableCell>
                    <TableCell>
                      {sim.assigned_device_id ? (
                        <Badge variant="secondary" className="gap-1">
                          <Smartphone className="h-3 w-3" />
                          {getDeviceName(sim.assigned_device_id)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Kein Gerät</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {sim.assigned_employee_id ? (
                        <Badge variant="secondary" className="gap-1">
                          <User className="h-3 w-3" />
                          {getEmployeeName(sim.assigned_employee_id)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Nicht zugewiesen</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openAssignDialog(sim)}
                          title="Zuweisen"
                        >
                          <User className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(sim)}
                          title="Bearbeiten"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSimToDelete(sim);
                            setDeleteDialogOpen(true);
                          }}
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSim ? "SIM-Karte bearbeiten" : "Neue SIM-Karte hinzufügen"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sim_number">SIM-Nummer (ICCID) *</Label>
                <Input
                  id="sim_number"
                  value={formData.sim_number}
                  onChange={(e) => setFormData({ ...formData, sim_number: e.target.value })}
                  placeholder="89490..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone_number">Telefonnummer</Label>
                <Input
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="+49 151..."
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="provider">Provider *</Label>
                <Select
                  value={formData.provider}
                  onValueChange={(value) => setFormData({ ...formData, provider: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Provider wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {SIM_PROVIDERS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tariff">Tarif</Label>
                <Input
                  id="tariff"
                  value={formData.tariff}
                  onChange={(e) => setFormData({ ...formData, tariff: e.target.value })}
                  placeholder="z.B. Business L"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pin">PIN</Label>
                <div className="relative">
                  <Input
                    id="pin"
                    type={showPin ? "text" : "password"}
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                    placeholder="****"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPin(!showPin)}
                  >
                    {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="puk">PUK</Label>
                <div className="relative">
                  <Input
                    id="puk"
                    type={showPuk ? "text" : "password"}
                    value={formData.puk}
                    onChange={(e) => setFormData({ ...formData, puk: e.target.value })}
                    placeholder="********"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPuk(!showPuk)}
                  >
                    {showPuk ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="monthly_cost">Monatliche Kosten (€)</Label>
                <Input
                  id="monthly_cost"
                  type="number"
                  step="0.01"
                  value={formData.monthly_cost}
                  onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_volume">Datenvolumen</Label>
                <Input
                  id="data_volume"
                  value={formData.data_volume}
                  onChange={(e) => setFormData({ ...formData, data_volume: e.target.value })}
                  placeholder="z.B. 10 GB oder Unlimited"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contract_start_date">Vertragsbeginn</Label>
                <Input
                  id="contract_start_date"
                  type="date"
                  value={formData.contract_start_date}
                  onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract_end_date">Vertragsende</Label>
                <Input
                  id="contract_end_date"
                  type="date"
                  value={formData.contract_end_date}
                  onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assigned_device_id">Gerät zuweisen</Label>
                <Select
                  value={formData.assigned_device_id}
                  onValueChange={(value) => setFormData({ ...formData, assigned_device_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Gerät wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Gerät</SelectItem>
                    {devices.map((device) => (
                      <SelectItem key={device.id} value={device.id}>
                        {device.phone_type} {device.model && `(${device.model})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assigned_employee_id">Mitarbeiter zuweisen</Label>
                <Select
                  value={formData.assigned_employee_id}
                  onValueChange={(value) => setFormData({ ...formData, assigned_employee_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Mitarbeiter wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nicht zugewiesen</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.position})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingSim ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>SIM-Karte zuweisen</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              SIM: {simToAssign?.sim_number} ({simToAssign?.provider})
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="device_select">Gerät</Label>
              <Select
                value={selectedDeviceId}
                onValueChange={setSelectedDeviceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Gerät wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Gerät</SelectItem>
                  {devices.map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.phone_type} {device.model && `(${device.model})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employee_select">Mitarbeiter</Label>
              <Select
                value={selectedEmployeeId}
                onValueChange={setSelectedEmployeeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Zuweisung entfernen</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.position})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAssign}>Zuweisen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>SIM-Karte löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die SIM-Karte "{simToDelete?.sim_number}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
